import React, { useEffect, useState } from "react";
import { useAiPanelStore } from "../store/aiPanelStore";
import { useAIContext } from "../store/aiContextStore";
import { fetchAIContextInsights } from "../api/aiContextAdvisorApi";
import { aiAsk } from "../api/aiAdvisorApi";
import LoadingSpinner from "./common/LoadingSpinner";
import ErrorBanner from "./common/ErrorBanner";

/**
 * Bottom AI Panel - Context-aware insights display.
 * 
 * Features:
 * - Shows AI insights based on active panel and inputs
 * - Auto-updates when panel or inputs change
 * - Only visible when enabled (license-gated)
 * - EVATlabs futuristic styling with Orbitron font
 */
export default function BottomAIPanel() {
  const enabled = useAiPanelStore((s) => s.enabled);
  const open = useAiPanelStore((s) => s.open);
  const toggle = useAiPanelStore((s) => s.toggle);
  const activePanel = useAIContext((state) => state.activePanel);
  const inputs = useAIContext((state) => state.inputs);
  
  const [insights, setInsights] = useState<string[]>([]);
  const [input, setInput] = useState("");
  const [log, setLog] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState<Error | null>(null);

  // Memoize inputs JSON to prevent infinite loops
  const inputsKey = React.useMemo(() => JSON.stringify(inputs), [inputs]);

  useEffect(() => {
    if (!enabled || !activePanel) {
      setInsights([]);
      setInsightsError(null);
      return;
    }

    let cancelled = false;
    setInsightsLoading(true);
    setInsightsError(null);
    
    fetchAIContextInsights(activePanel, inputs)
      .then((res) => {
        if (!cancelled) {
          setInsights(res);
          setInsightsError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          const error = err instanceof Error ? err : new Error('Failed to fetch AI insights');
          setInsightsError(error);
          setInsights([]);
          console.error('AI insights fetch failed:', err);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setInsightsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, activePanel, inputsKey]);

  if (!enabled) {
    return null;
  }

  const handleAsk = async () => {
    if (!input.trim() || loading) return;
    const query = input.trim();
    setLoading(true);
    setInput("");
    
    try {
      const res = await aiAsk(query);
      setLog((l) => [...l, `> ${query}`, res]);
    } catch (err) {
      setLog((l) => [...l, `> ${query}`, `Error: ${err instanceof Error ? err.message : String(err)}`]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleAsk();
    }
  };

  return (
    <div
      className="bottom-ai-panel"
      style={{
        transform: open ? "translateY(0)" : "translateY(calc(100% - 60px))",
        transition: "transform 0.3s ease-out",
      }}
      aria-live="polite"
      aria-label="ESG AI Assistant"
    >
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <strong style={{ color: "#1dff9d" }}>AI ESG Advisor ({activePanel || "Idle"})</strong>
        <button 
          onClick={toggle} 
          style={{ color: "#1dff9d", background: "none", border: "none", cursor: "pointer", padding: "4px 8px" }}
          aria-label={open ? "Collapse panel" : "Expand panel"}
        >
          {open ? "▼" : "▲"}
        </button>
      </div>

      <div style={{ overflowY: "auto", maxHeight: "100px", fontSize: "12px", marginBottom: 8, paddingRight: 4 }}>
        {insightsError && (
          <div style={{ marginBottom: 8, padding: 4, border: "1px solid #ff4444", borderRadius: 4, background: "#ff444411" }}>
            <div style={{ color: "#ff4444", fontSize: "10px" }}>⚠️ {insightsError.message}</div>
          </div>
        )}
        {insightsLoading && (
          <div style={{ marginBottom: 8, padding: 4 }}>
            <div style={{ color: "#1dff9d", fontSize: "10px" }}>Loading insights...</div>
          </div>
        )}
        {!insightsLoading && insights.length > 0 && (
          <div style={{ marginBottom: 8, paddingBottom: 8, borderBottom: "1px solid #1dff9d33" }}>
            <strong style={{ color: "#0f0", fontSize: "11px" }}>Context Insights:</strong>
            {insights.map((i, idx) => (
              <div key={`insight-${idx}`} style={{ color: "#0f0", fontSize: "11px", marginTop: 4 }}>{i}</div>
            ))}
          </div>
        )}
        {log.length > 0 && (
          <div>
            {log.map((l, i) => (
              <div key={`log-${i}`} style={{ marginBottom: 4, color: l.startsWith('>') ? '#fff' : '#ccc', fontSize: "12px" }}>
                {l}
              </div>
            ))}
          </div>
        )}
        {insights.length === 0 && log.length === 0 && (
          <div style={{ color: "#666", fontSize: "11px" }}>No insights yet. Ask a question or switch to a different view.</div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <input
          type="text"
          style={{ 
            flex: 1, 
            background: "#1a1a1a", 
            border: "1px solid #1dff9d33", 
            color: "#fff", 
            padding: "6px 8px", 
            borderRadius: "4px",
            fontSize: "12px"
          }}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask ESG impact, emissions, compliance..."
          disabled={loading}
        />
        <button 
          disabled={loading || !input.trim()} 
          onClick={handleAsk} 
          style={{ 
            width: 70, 
            background: loading || !input.trim() ? "#333" : "#1dff9d", 
            color: loading || !input.trim() ? "#666" : "#000", 
            border: "none", 
            borderRadius: "4px", 
            cursor: loading || !input.trim() ? "not-allowed" : "pointer", 
            padding: "6px",
            fontSize: "12px"
          }}
        >
          {loading ? "..." : "Send"}
        </button>
      </div>
    </div>
  );
}
